import { TableColumn } from "@models/components/tableColumn";
import { ClientDetailsService } from "@services/clients/clients.details/clientdetails.service";
import { DropdownOptionsService } from "@services/clients/dropdownOptions.service";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { EmailAddress } from "@models/clients/emailAddress";
import { PreviousProfessionalActivity } from "@models/clients/clients.details/previousProfessionalActivity";
import { AdditionalProfileDetails } from "@models/clients/clients.details/additionalProfileDetails";
import { clientNumberKeyPress, clientNumberPaste, watchClientNumber } from "@common/utils/clientNumberUtils";


export class ClientDetailsProfileController {
    public client: any;
    public clientNumber: string; // Add clientNumber binding
    public isViewOnly: boolean = false;
    // Properties passed from parent
    public onSave: () => void;
    public isSaving: boolean = false;
    public lastSaved: Date | null = null;
    public isLoading: boolean = false;
    public professionalActivities: PreviousProfessionalActivity[] = [];
    public additionalProfiles: AdditionalProfileDetails[] = [];
    public additionalProfileFilter: string = '';
        
    // Selection properties for professional activities table
    public selectAllProfessional: boolean = false;
    public selectedProfessionalItems: any[] = [];
    
    // Selection properties for additional profiles table
    public selectAllAdditional: boolean = false;
    public selectedAdditionalItems: any[] = [];
    
    // Email address properties
    public emailAddresses: EmailAddress[] = [];
    public isLoadingEmails: boolean = false;
    public selectAllEmails: boolean = false;
    public selectedEmailItems: EmailAddress[] = [];

    // Professional activities properties
    public isLoadingProfessionalActivities: boolean = false;

    // Additional profiles properties
    public isLoadingAdditionalProfiles: boolean = false;

    // Connections properties
    public connections: any[] = [];
    public isLoadingConnections: boolean = false;

    // Dropdown options
    public dropdownOptions: any = {};

    // Dynamic phone fields for Profile Details
    public profileSelectedPhoneTypes: string[] = [];
    public availableProfilePhoneTypes: string[] = ['residence', 'mobile', 'business'];

    // Dynamic phone fields for Emergency Contact
    public emergencySelectedPhoneTypes: string[] = [];
    public availableEmergencyPhoneTypes: string[] = ['residence', 'mobile', 'business'];

    /** @ngInject */
    constructor(
        private $mdDialog: angular.material.IDialogService,
        private $rootScope: angular.IRootScopeService,
        private $scope: angular.IScope,
        private dropdownOptionsService: DropdownOptionsService,
        private clientDetailsService: ClientDetailsService,
        private clientCRMRepo: ClientCRMRepo,
        private notifyjs: any
    ) {}

    // Column definitions for professional activities table
    public professionalActivityColumns: TableColumn[] = [
        { key: 'name', label: 'Professional Activity Name', type: 'text' },
        { key: 'nameOfOrganisation', label: 'Name of Organisation', type: 'text' },
        { key: 'periodFrom', label: 'Period From', type: 'date' },
        { key: 'periodTo', label: 'Period To', type: 'date' },
    ];

    // Column definitions for additional profiles table
    public additionalProfileColumns = [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'typeOfPortfolioName', label: 'Type of Portfolio', sortable: true },
        { key: 'employmentStatusName', label: 'Employment Status', type: 'text' },
        { key: 'professionalActivityName', label: 'Professional Activity', type: 'text' },
        { key: 'businessPhoneNumber', label: 'Business Phone Number', type: 'text' },
        { key: 'mobilePhone', label: 'Mobile Phone', type: 'text' },
        { key: 'residencePhone', label: 'Residence Phone', type: 'text' },
    ];

    // Column definitions for email addresses table
    public emailAddressColumns: TableColumn[] = [
        { key: 'email', label: 'Email Address', type: 'text' },
    ];

    // Column definitions for connections table
    public connectionColumns: TableColumn[] = [
        { key: 'connectionCategoryName', label: 'Connection Category', type: 'text' },
        { key: 'titleName', label: 'Title', type: 'text' },
        { key: 'firstName', label: 'First Name', type: 'text' },
        { key: 'lastName', label: 'Last Name', type: 'text' },
        { key: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
        { key: 'emailAddress', label: 'Email Address', type: 'text' },
        { key: 'mobilePhone', label: 'Mobile Phone', type: 'text' },
        { key: 'residentialAddress', label: 'Residential Address', type: 'text' },
    ];

    
    public $onInit(): void {
        // Show loading if client data isn't available yet
        this.isLoading = !this.client;
        
        // Initialize empty arrays for the table sections
        this.professionalActivities = [];
        this.additionalProfiles = [];
        this.emailAddresses = [];
        
        // Listen for dropdown options loaded event from parent
        this.$scope.$on('dropdownOptionsLoaded', (event: any, options: any) => {
            this.loadDropdownOptions(options);
        });
        
        // Try to get options immediately if they're already loaded
        this.dropdownOptionsService.getAllOptions()
            .then((options) => {
                this.loadDropdownOptions(options);
            })
            .catch((error) => {
                console.warn('Failed to get dropdown options immediately:', error);
            });
        
        this.loadEmailAddresses();
        this.loadProfessionalActivities();
        this.loadAdditionalProfiles();
        this.loadConnections();
        
        // Initialize phone fields if client is already available
        if (this.client) {
            this.initializePhoneFields();
        }

        // Watch for client data to initialize phone fields
        this.$scope.$watch(() => this.client, (newClient: any) => {
            if (newClient) {
                this.initializePhoneFields();
            }
        }, true);

        // Watch for client number changes to validate it doesn't start with 0
        this.$scope.$watch(() => this.client && this.client.clientNumber, (newValue: string, oldValue: string) => {
            watchClientNumber(newValue, oldValue, this.client, this.notifyjs);
        });
    }

    public $onChanges(changes: any): void {
        if (changes.client && changes.client.currentValue) {
            this.isLoading = false; // Hide loading when client data arrives
            this.initializePhoneFields(); // Reinitialize phone fields when client data changes
        }
    }

    public addNewProfessionalActivity(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.professionalActivity = {
            professionalActivityName: '',
            nameOfOrganisation: '',
            periodFrom: '',
            periodTo: '',
            comments: '',
            individualName: (this.client && this.client.name) || '',
            institutionName: ''
        };
        
        // Pass dropdown options to the modal
        tempScope.dropdownOptions = this.dropdownOptions;

        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="50"><new-professional-activity-modal client="client" professional-activity="professionalActivity" dropdown-options="dropdownOptions"></new-professional-activity-modal></md-dialog>',
                scope: tempScope,
            })
            .then((result) => {
                if (result) {
                    this.professionalActivitySaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening professional activity modal:', error);
            });
    }

    public addNewAdditionalProfile(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.additionalProfile = {
            name: '',
            typeOfPortfolio: '',
            employmentStatus: '',
            professionalActivity: '',
            organisation: '',
            dateJoined: '',
            addressOfOrganisation: '',
            residentialAddress: '',
            comments: '',
            dateOfBirth: '',
            residencePhone: '',
            mobilePhone: '',
            businessPhoneNumber: '',
            emailAddress1: '',
            emailAddress2: '',
            emailAddress3: '',
            website: '',
            jurisdiction: '',
            geographicalLocation: '',
            linkedinUrl: ''
        };
        
        // Pass dropdown options to the modal
        tempScope.dropdownOptions = this.dropdownOptions;

        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="70"><new-additional-profile-modal client="client" additional-profile="additionalProfile" dropdown-options="dropdownOptions"></new-additional-profile-modal></md-dialog>',
                scope: tempScope,
            })
            .then((result) => {
                if (result) {
                    this.additionalProfileSaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening additional profile modal:', error);
            });
    }

    public addNewEmailAddress(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.emailAddress = null; // Will be initialized in the modal

        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="30"><new-email-address-modal client="client" email-address="emailAddress"></new-email-address-modal></md-dialog>',
                scope: tempScope,
            })
            .then((result) => {
                if (result) {
                    this.loadEmailAddresses();
                    // this.emailAddressSaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening email address modal:', error);
            });
    }

    public editProfessionalActivity(professionalActivity: PreviousProfessionalActivity): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.professionalActivity = professionalActivity;
        
        // Pass dropdown options to the modal
        tempScope.dropdownOptions = this.dropdownOptions;

        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="50"><new-professional-activity-modal client="client" professional-activity="professionalActivity" dropdown-options="dropdownOptions"></new-professional-activity-modal></md-dialog>',
                scope: tempScope,
            })
            .then((result) => {
                if (result) {
                    this.professionalActivitySaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening professional activity modal:', error);
            });
    }

    public editAdditionalProfile(additionalProfile: AdditionalProfileDetails): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.additionalProfile = additionalProfile;
        tempScope.dropdownOptions = this.dropdownOptions;

        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="70"><new-additional-profile-modal client="client" additional-profile="additionalProfile" dropdown-options="dropdownOptions"></new-additional-profile-modal></md-dialog>',
                scope: tempScope,
            })
            .then((result) => {
                if (result) {
                    this.additionalProfileSaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening additional profile modal:', error);
            });
    }

    public editEmailAddress(emailAddress: EmailAddress): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.emailAddress = emailAddress;

        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="30"><new-email-address-modal client="client" email-address="emailAddress"></new-email-address-modal></md-dialog>',
                scope: tempScope,
            })
            .then((result) => {
                if (result) {
                    // fetch the updated email address from the backend
                    this.loadEmailAddresses();
                    // this.emailAddressUpdated(result);
                }
            })
            .catch((error) => {
                console.error('Error opening email address modal:', error);
            });
    }

    public deleteSelectedEmailAddresses(itemIds: number[]): angular.IPromise<void> | void {
        if (itemIds.length === 0) {
            return;
        }

        const confirm = this.$mdDialog.confirm()
            .title('Delete Email Addresses')
            .textContent(`Are you sure you want to delete ${itemIds.length} email address(es)?`)
            .ok('Delete')
            .cancel('Cancel');

        return this.$mdDialog.show(confirm).then(() => {
            this.isLoadingEmails = true;
            return this.clientCRMRepo.deleteEmailAddresses(itemIds)
                .then(() => {
                    this.emailAddresses = this.emailAddresses.filter(item => !itemIds.includes(item.id));
                    this.isLoadingEmails = false;
                })
                .catch((error) => {
                    console.error('Error deleting email addresses:', error);
                    this.isLoadingEmails = false;
                    throw error;
                });
        });
    }

    public deleteSelectedProfessionalActivities(itemIds: number[]): angular.IPromise<void> | void {
        if (itemIds.length === 0) {
            return;
        }

        const confirm = this.$mdDialog.confirm()
            .title('Delete Professional Activities')
            .textContent(`Are you sure you want to delete ${itemIds.length} previous professional activit(ies)?`)
            .ok('Delete')
            .cancel('Cancel');

        return this.$mdDialog.show(confirm).then(() => {
            return this.clientCRMRepo.deletePreviousProfessionalActivities(itemIds)
                .then(() => {
                    this.loadProfessionalActivities();
                })
                .catch((error) => {
                    console.error('Error deleting professional activities:', error);
                    throw error;
                });
        });
    }

    public deleteSelectedAdditionalProfiles(itemIds: number[]): angular.IPromise<void> | void {
        if (itemIds.length === 0) {
            return;
        }

        const confirm = this.$mdDialog.confirm()
            .title('Delete Additional Profiles')
            .textContent(`Are you sure you want to delete ${itemIds.length} additional profile(s)?`)
            .ok('Delete')
            .cancel('Cancel');

        return this.$mdDialog.show(confirm).then(() => {
            this.isLoadingAdditionalProfiles = true;
            return this.clientCRMRepo.deleteAdditionalProfileDetails(itemIds)
                .then(() => {
                    this.additionalProfiles = this.additionalProfiles.filter(item => !itemIds.includes(item.id));
                    this.isLoadingAdditionalProfiles = false;
                })
                .catch((error) => {
                    console.error('Error deleting additional profiles:', error);
                    this.isLoadingAdditionalProfiles = false;
                    throw error;
                });
        });
    }

    // Selection handling methods for professional activities table
    public toggleSelectAllProfessional(selected: boolean): void {
        this.selectAllProfessional = selected;
        if (selected) {
            this.selectedProfessionalItems = [...this.professionalActivities];
        } else {
            this.selectedProfessionalItems = [];
        }
    }

    public updateSelectionProfessional(item: any, selected: boolean): void {
        if (selected) {
            this.selectedProfessionalItems.push(item);
        } else {
            const index = this.selectedProfessionalItems.findIndex(selectedItem => selectedItem === item);
            if (index > -1) {
                this.selectedProfessionalItems.splice(index, 1);
            }
        }
        // Update selectAll state based on selection
        this.selectAllProfessional = this.selectedProfessionalItems.length === this.professionalActivities.length;
    }

    // Selection handling methods for additional profiles table
    public toggleSelectAllAdditional(selected: boolean): void {
        this.selectAllAdditional = selected;
        if (selected) {
            this.selectedAdditionalItems = [...this.additionalProfiles];
        } else {
            this.selectedAdditionalItems = [];
        }
    }

    public updateSelectionAdditional(item: any, selected: boolean): void {
        if (selected) {
            this.selectedAdditionalItems.push(item);
        } else {
            const index = this.selectedAdditionalItems.findIndex(selectedItem => selectedItem === item);
            if (index > -1) {
                this.selectedAdditionalItems.splice(index, 1);
            }
        }
        // Update selectAll state based on selection
        this.selectAllAdditional = this.selectedAdditionalItems.length === this.additionalProfiles.length;
    }

    // Selection handling methods for email addresses table
    public toggleSelectAllEmails(selected: boolean): void {
        this.selectAllEmails = selected;
        if (selected) {
            this.selectedEmailItems = [...this.emailAddresses];
        } else {
            this.selectedEmailItems = [];
        }
    }

    public updateSelectionEmails(item: any, selected: boolean): void {
        if (selected) {
            this.selectedEmailItems.push(item);
        } else {
            const index = this.selectedEmailItems.findIndex(selectedItem => selectedItem === item);
            if (index > -1) {
                this.selectedEmailItems.splice(index, 1);
            }
        }
        // Update selectAll state based on selection
        this.selectAllEmails = this.selectedEmailItems.length === this.emailAddresses.length;
    }

    private professionalActivitySaved(result: any) {
        if (result && this.client && this.client.id) {
            this.isLoadingProfessionalActivities = true;
            this.clientCRMRepo.savePreviousProfessionalActivity(this.client.id, result)
                .then((response: any) => {
                    // Reload the professional activities from the backend
                    this.loadProfessionalActivities();
                    this.isLoadingProfessionalActivities = false;
                })
                .catch((error) => {
                    console.error('Error saving professional activity:', error);
                    this.isLoadingProfessionalActivities = false;
                });
        }
    }

    private additionalProfileSaved(result: any) {
        if (result && this.client && this.client.id) {
            this.isLoadingAdditionalProfiles = true;
            console.log('Saving additional profile:', result);
            this.clientCRMRepo.saveAdditionalProfileDetails(this.client.id, result)
                .then((response: any) => {
                    // Reload the additional profiles from the backend
                    this.loadAdditionalProfiles();
                    this.isLoadingAdditionalProfiles = false;
                })
                .catch((error) => {
                    console.error('Error saving additional profile:', error);
                    this.isLoadingAdditionalProfiles = false;
                });
        }
    }

    private loadEmailAddresses(): void {
        const clientNumber = this.clientNumber;
        if (clientNumber) {
            this.isLoadingEmails = true;
            this.clientCRMRepo.getEmailAddresses(clientNumber)
                .then((response: any) => {
                    this.emailAddresses = response.data || [];
                    this.isLoadingEmails = false;
                })
                .catch((error) => {
                    console.error('Error loading email addresses:', error);
                    this.isLoadingEmails = false;
                });
        }
    }

    private loadProfessionalActivities(): void {
        const clientNumber = this.clientNumber;
        if (clientNumber) {
            this.isLoadingProfessionalActivities = true;
            this.clientCRMRepo.getPreviousProfessionalActivities(clientNumber)
                .then((response: any) => {
                    this.professionalActivities = response.data || [];
                    this.isLoadingProfessionalActivities = false;
                })
                .catch((error) => {
                    console.error('Error loading professional activities:', error);
                    this.isLoadingProfessionalActivities = false;
                });
        }
    }

    private loadAdditionalProfiles(): void {
        const clientNumber = this.clientNumber;
        if (clientNumber) {
            this.isLoadingAdditionalProfiles = true;
            this.clientCRMRepo.getAdditionalProfileDetails(clientNumber)
                .then((response: any) => {
                    this.additionalProfiles = response.data || [];
                    this.isLoadingAdditionalProfiles = false;
                })
                .catch((error) => {
                    console.error('Error loading additional profiles:', error);
                    this.isLoadingAdditionalProfiles = false;
                });
        }
    }



    private loadDropdownOptions(options: any): void {
        // Store all options in the single dropdownOptions object
        this.dropdownOptions = options;
    }

    // Connections methods
    public loadConnections(): void {
        const clientNumber = this.clientNumber;
        
        if (clientNumber && clientNumber !== 'Individual' && clientNumber !== 'Institution') {
            this.isLoadingConnections = true;
            this.clientCRMRepo.getConnectionsContacts(clientNumber)
                .then((response: any) => {
                    this.connections = response.data || [];
                    this.isLoadingConnections = false;
                })
                .catch((error) => {
                    console.error('Error loading connections:', error);
                    this.isLoadingConnections = false;
                });
        }
    }

    public addConnection(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.connection = {
            status: 'active',
            selected: false
        };
        
        // Pass dropdown options to the modal
        tempScope.dropdownOptions = this.dropdownOptions;

        this.$mdDialog
            .show({
                template: '<md-dialog flex="70"><add-connection-modal client="client" connection="connection" dropdown-options="dropdownOptions"></add-connection-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                if (result) {
                    this.connectionSaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening connection modal:', error);
            });
    }

    public editConnection(item: any): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.connection = item;
        tempScope.dropdownOptions = this.dropdownOptions;

        this.$mdDialog
            .show({
                template: '<md-dialog flex="70"><add-connection-modal client="client" connection="connection" dropdown-options="dropdownOptions"></add-connection-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                if (result) {
                    this.connectionSaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening connection modal:', error);
            });
    }

    public deleteSelectedConnections(itemIds: number[]): angular.IPromise<void> | void {
        if (this.isViewOnly) return;
        
        if (itemIds.length === 0) {
            return;
        }

        const confirm = this.$mdDialog.confirm()
            .title('Delete Connections')
            .textContent(`Are you sure you want to delete ${itemIds.length} connection(s)?`)
            .ok('Delete')
            .cancel('Cancel');

        return this.$mdDialog.show(confirm).then(() => {
            this.isLoadingConnections = true;
            return this.clientCRMRepo.deleteConnectionsContacts(itemIds)
                .then(() => {
                    this.connections = this.connections.filter(item => !itemIds.includes(item.id));
                    this.isLoadingConnections = false;
                })
                .catch((error) => {
                    console.error('Error deleting connections:', error);
                    this.isLoadingConnections = false;
                    throw error;
                });
        });
    }

    private connectionSaved(result: any): void {
        if (result && this.client && this.client.id) {
            this.isLoadingConnections = true;
            // This is a save/update action
            this.clientCRMRepo.saveConnectionContact(this.client.id, result)
                .then((response: any) => {
                    // Reload the connections from the backend
                    this.loadConnections();
                    this.isLoadingConnections = false;
                })
                .catch((error) => {
                    console.error('Error saving connection:', error);
                    this.isLoadingConnections = false;
                });
        }
    }

    public isIndividual(): boolean {
        return this.clientDetailsService.isIndividual(this.client);
    }

    public isInstitution(): boolean {
        return this.clientDetailsService.isInstitution(this.client);
    }

    public onKeyPress(event: KeyboardEvent): boolean {
        return clientNumberKeyPress(event, this.notifyjs);
    }

    public onPaste(event: ClipboardEvent): void {
        clientNumberPaste(event, this.notifyjs);
    }

    // Initialize phone fields from existing client data
    private initializePhoneFields(): void {
        if (!this.client) {
            return;
        }

        // Initialize Profile Details selected phone types (residence not offered for institutions)
        this.profileSelectedPhoneTypes = [];
        if (!this.isInstitution() && this.hasValue(this.client.residencePhone1)) {
            this.profileSelectedPhoneTypes.push('residence');
        }
        if (this.hasValue(this.client.profileMobilePhone)) {
            this.profileSelectedPhoneTypes.push('mobile');
        }
        if (this.hasValue(this.client.businessPhone)) {
            this.profileSelectedPhoneTypes.push('business');
        }

        // If none have data, default to mobile
        if (this.profileSelectedPhoneTypes.length === 0) {
            this.profileSelectedPhoneTypes.push('mobile');
        }

        // Initialize Emergency Contact selected phone types (residence not offered for institutions)
        this.emergencySelectedPhoneTypes = [];
        if (!this.isInstitution() && this.hasValue(this.client.emergencyResidencePhone)) {
            this.emergencySelectedPhoneTypes.push('residence');
        }
        if (this.hasValue(this.client.emergencyMobilePhone)) {
            this.emergencySelectedPhoneTypes.push('mobile');
        }
        if (this.hasValue(this.client.emergencyBusinessPhone)) {
            this.emergencySelectedPhoneTypes.push('business');
        }

        // If none have data, default to mobile
        if (this.emergencySelectedPhoneTypes.length === 0) {
            this.emergencySelectedPhoneTypes.push('mobile');
        }
    }

    // Helper method to check if a value is not empty
    private hasValue(value: any): boolean {
        if (value === null || value === undefined) {
            return false;
        }
        if (typeof value === 'string') {
            return value.trim().length > 0;
        }
        // Handle other types (numbers, etc.)
        return !!value;
    }

    // Check if a phone type should be disabled (has data - cannot be deselected; residence not available for institutions)
    public isProfilePhoneTypeDisabled(type: string): boolean {
        if (!this.client) {
            return false;
        }

        // Residence phone is not available for institutional clients
        if (type === 'residence' && this.isInstitution()) {
            return true;
        }

        // Disable if it has data (cannot be deselected)
        if (type === 'residence' && this.hasValue(this.client.residencePhone1)) {
            return true;
        }
        if (type === 'mobile' && this.hasValue(this.client.profileMobilePhone)) {
            return true;
        }
        if (type === 'business' && this.hasValue(this.client.businessPhone)) {
            return true;
        }
        
        return false;
    }

    // Check if a phone type should be disabled (has data - cannot be deselected; residence not available for institutions)
    public isEmergencyPhoneTypeDisabled(type: string): boolean {
        if (!this.client) {
            return false;
        }

        // Residence phone is not available for institutional clients
        if (type === 'residence' && this.isInstitution()) {
            return true;
        }
        
        // Disable if it has data (cannot be deselected)
        if (type === 'residence' && this.hasValue(this.client.emergencyResidencePhone)) {
            return true;
        }
        if (type === 'mobile' && this.hasValue(this.client.emergencyMobilePhone)) {
            return true;
        }
        if (type === 'business' && this.hasValue(this.client.emergencyBusinessPhone)) {
            return true;
        }
        
        return false;
    }

    // Check if a phone type is selected in Profile Details
    public isProfilePhoneTypeSelected(type: string): boolean {
        return this.profileSelectedPhoneTypes.includes(type);
    }

    // Check if a phone type is selected in Emergency Contact
    public isEmergencyPhoneTypeSelected(type: string): boolean {
        return this.emergencySelectedPhoneTypes.includes(type);
    }

    // Handle profile phone type selection change
    public onProfilePhoneTypesChange(): void {
        if (!this.client) {
            return;
        }

        // Prevent deselecting options that have data
        if (this.hasValue(this.client.residencePhone1) && !this.profileSelectedPhoneTypes.includes('residence')) {
            this.profileSelectedPhoneTypes.push('residence');
        }
        if (this.hasValue(this.client.profileMobilePhone) && !this.profileSelectedPhoneTypes.includes('mobile')) {
            this.profileSelectedPhoneTypes.push('mobile');
        }
        if (this.hasValue(this.client.businessPhone) && !this.profileSelectedPhoneTypes.includes('business')) {
            this.profileSelectedPhoneTypes.push('business');
        }

        // Clear phone fields that are no longer selected (only if they don't have data)
        if (!this.profileSelectedPhoneTypes.includes('residence') && !this.hasValue(this.client.residencePhone1)) {
            this.client.residencePhone1 = '';
        }
        if (!this.profileSelectedPhoneTypes.includes('mobile') && !this.hasValue(this.client.profileMobilePhone)) {
            this.client.profileMobilePhone = '';
        }
        if (!this.profileSelectedPhoneTypes.includes('business') && !this.hasValue(this.client.businessPhone)) {
            this.client.businessPhone = '';
        }
    }

    // Handle emergency phone type selection change
    public onEmergencyPhoneTypesChange(): void {
        if (!this.client) {
            return;
        }

        // Prevent deselecting options that have data
        if (this.hasValue(this.client.emergencyResidencePhone) && !this.emergencySelectedPhoneTypes.includes('residence')) {
            this.emergencySelectedPhoneTypes.push('residence');
        }
        if (this.hasValue(this.client.emergencyMobilePhone) && !this.emergencySelectedPhoneTypes.includes('mobile')) {
            this.emergencySelectedPhoneTypes.push('mobile');
        }
        if (this.hasValue(this.client.emergencyBusinessPhone) && !this.emergencySelectedPhoneTypes.includes('business')) {
            this.emergencySelectedPhoneTypes.push('business');
        }

        // Clear phone fields that are no longer selected (only if they don't have data)
        if (!this.emergencySelectedPhoneTypes.includes('residence') && !this.hasValue(this.client.emergencyResidencePhone)) {
            this.client.emergencyResidencePhone = '';
        }
        if (!this.emergencySelectedPhoneTypes.includes('mobile') && !this.hasValue(this.client.emergencyMobilePhone)) {
            this.client.emergencyMobilePhone = '';
        }
        if (!this.emergencySelectedPhoneTypes.includes('business') && !this.hasValue(this.client.emergencyBusinessPhone)) {
            this.client.emergencyBusinessPhone = '';
        }
    }

    // Get phone type label
    public getPhoneTypeLabel(type: string): string {
        const labels: {[key: string]: string} = {
            'residence': 'Residence Phone',
            'mobile': 'Mobile Phone',
            'business': 'Business Phone'
        };
        return labels[type] || type;
    }

    // Get ordered phone types for Profile Details (prioritize those with data first)
    public getOrderedProfilePhoneTypes(): string[] {
        if (!this.client) {
            return [];
        }

        const selected = this.profileSelectedPhoneTypes.slice();
        const withData: string[] = [];
        const withoutData: string[] = [];

        selected.forEach(type => {
            let hasValue = false;
            if (type === 'residence' && this.hasValue(this.client.residencePhone1)) {
                hasValue = true;
            } else if (type === 'mobile' && this.hasValue(this.client.profileMobilePhone)) {
                hasValue = true;
            } else if (type === 'business' && this.hasValue(this.client.businessPhone)) {
                hasValue = true;
            }

            if (hasValue) {
                withData.push(type);
            } else {
                withoutData.push(type);
            }
        });

        // If only one has data, that one goes first
        // Otherwise, order: residence, mobile, business
        const order = ['residence', 'mobile', 'business'];
        const sorted = [...withData, ...withoutData];
        sorted.sort((a, b) => {
            const aIndex = order.indexOf(a);
            const bIndex = order.indexOf(b);
            return aIndex - bIndex;
        });

        return sorted;
    }

    // Get ordered phone types for Emergency Contact (prioritize those with data first)
    public getOrderedEmergencyPhoneTypes(): string[] {
        if (!this.client) {
            return [];
        }

        const selected = this.emergencySelectedPhoneTypes.slice();
        const withData: string[] = [];
        const withoutData: string[] = [];

        selected.forEach(type => {
            let hasValue = false;
            if (type === 'residence' && this.hasValue(this.client.emergencyResidencePhone)) {
                hasValue = true;
            } else if (type === 'mobile' && this.hasValue(this.client.emergencyMobilePhone)) {
                hasValue = true;
            } else if (type === 'business' && this.hasValue(this.client.emergencyBusinessPhone)) {
                hasValue = true;
            }

            if (hasValue) {
                withData.push(type);
            } else {
                withoutData.push(type);
            }
        });

        // If only one has data, that one goes first
        // Otherwise, order: residence, mobile, business
        const order = ['residence', 'mobile', 'business'];
        const sorted = [...withData, ...withoutData];
        sorted.sort((a, b) => {
            const aIndex = order.indexOf(a);
            const bIndex = order.indexOf(b);
            return aIndex - bIndex;
        });

        return sorted;
    }
}

export const ClientDetailsProfileComponent: angular.IComponentOptions = {
    template: require('./clientdetails.profile.html'),
    controller: ClientDetailsProfileController,
    controllerAs: '$ctrl',
    bindings: {
        client: '=',
        isSaving: '<',
        lastSaved: '<',
        clientNumber: '<',
        isViewOnly: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/profile/clientdetails.profile.ts